import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Card,
  Table,
  Typography,
  Tooltip,
  Spin,
  Badge,
  Empty,
  Tag,
  Progress,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  TrophyOutlined,
  HistoryOutlined,
  FileTextOutlined,
  FileOutlined,
  TagsOutlined,
  FolderOutlined
} from '@ant-design/icons';
import DB from '../services/dbService';
import '../styles/data-center.css';

const { Text } = Typography;

// 标签颜色列表 - Apple风格色彩
const tagColors = [
  '#0A84FF', '#30D158', '#5E5CE6', '#FF9F0A', '#FF453A',
  '#64D2FF', '#BF5AF2', '#FF375F', '#FFD60A', '#5AC8FA'
];

const UsageStats = forwardRef((props, ref) => {
  const [mostUsedPrompts, setMostUsedPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagStats, setTagStats] = useState({
    topTags: [],
    tagCategories: []
  });
  const [versionStats, setVersionStats] = useState({
    avgVersionsPerPrompt: 0,
    promptsWithMultipleVersions: 0
  });
  const [generalStats, setGeneralStats] = useState({
    promptCount: 0,
    tagCount: 0,
    folderCount: 0
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      // 获取使用排行榜数据
      const prompts = await DB.getMostUsedPrompts();
      setMostUsedPrompts(prompts);
      
      // 获取统计数据
      const stats = await DB.getStats();
      setTagStats({
        topTags: stats.topTags || [],
        tagCategories: stats.tagCategories || []
      });
      setVersionStats({
        avgVersionsPerPrompt: stats.avgVersionsPerPrompt || 0,
        promptsWithMultipleVersions: stats.promptsWithMultipleVersions || 0
      });
      setGeneralStats({
        promptCount: stats.promptCount || 0,
        tagCount: stats.tagCount || 0,
        folderCount: stats.folderCount || 0
      });
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 暴露刷新方法给父组件
  useImperativeHandle(ref, () => ({
    refreshData: fetchData
  }));

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      title: '排名',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (_, __, index) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {index < 3 ? (
            <TrophyOutlined 
              style={{ 
                color: ['#FFD60A', '#C0C0C0', '#FF9F0A'][index],
                fontSize: '20px',
                marginRight: '8px',
                filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))'
              }} 
            />
          ) : null}
          <span style={{ fontWeight: 500 }}>{index + 1}</span>
        </div>
      ),
    },
    {
      title: '提示词标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text}>
          <div style={{ 
            maxWidth: '180px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            fontWeight: 500
          }}>
            {text}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '复制次数',
      dataIndex: 'copyCount',
      key: 'copyCount',
      width: 100,
      render: (count) => (
        <div style={{ 
          fontWeight: 'bold', 
          color: '#0A84FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(10, 132, 255, 0.08)',
          borderRadius: '16px',
          padding: '4px 8px'
        }}>
          {count || 0}
        </div>
      ),
    },
  ];

  // 渲染总体统计卡片
  const renderGeneralStats = () => (
    <Row gutter={[16, 16]} className="fade-in" style={{ marginBottom: '24px' }}>
      <Col span={8}>
        <Card className="apple-card stat-card delay-1">
          <Statistic
            title={
              <div className="stat-title-container">
                <div className="icon-wrapper icon-blue">
                  <FileOutlined />
                </div>
                <span>提示词</span>
              </div>
            }
            value={generalStats.promptCount}
            valueStyle={{ color: '#0A84FF' }}
          />
        </Card>
      </Col>
      <Col span={8}>
        <Card className="apple-card stat-card delay-2">
          <Statistic
            title={
              <div className="stat-title-container">
                <div className="icon-wrapper icon-purple">
                  <TagsOutlined />
                </div>
                <span>标签</span>
              </div>
            }
            value={generalStats.tagCount}
            valueStyle={{ color: '#BF5AF2' }}
          />
        </Card>
      </Col>
      <Col span={8}>
        <Card className="apple-card stat-card delay-3">
          <Statistic
            title={
              <div className="stat-title-container">
                <div className="icon-wrapper icon-green">
                  <FolderOutlined />
                </div>
                <span>文件夹</span>
              </div>
            }
            value={generalStats.folderCount}
            valueStyle={{ color: '#30D158' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // 渲染版本统计内容
  const renderVersionAnalysis = () => (
    <Spin spinning={loading}>
      <Card className="apple-card fade-in delay-2" style={{ marginBottom: '24px' }}>
        <div className="title-container">
          <Text className="section-title title-blue">版本管理统计</Text>
          <Text className="subtitle">历史版本追踪</Text>
        </div>
        
        {generalStats.promptCount > 0 ? (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card className="apple-card" style={{ background: 'rgba(10, 132, 255, 0.05)' }}>
                <Statistic
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <div className="icon-wrapper icon-blue" style={{ width: '24px', height: '24px', flexShrink: 0 }}>
                        <HistoryOutlined style={{ fontSize: '12px' }} />
                      </div>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'visible' }}>平均版本数</span>
                    </div>
                  }
                  value={versionStats.avgVersionsPerPrompt}
                  precision={1}
                  valueStyle={{ color: '#0A84FF', fontSize: '24px' }}
                  suffix={<span style={{ fontSize: '14px' }}>/ 提示词</span>}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card className="apple-card" style={{ background: 'rgba(10, 132, 255, 0.05)' }}>
                <Statistic
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <div className="icon-wrapper icon-blue" style={{ width: '24px', height: '24px', flexShrink: 0 }}>
                        <FileTextOutlined style={{ fontSize: '12px' }} />
                      </div>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'visible' }}>多版本提示词</span>
                    </div>
                  }
                  value={versionStats.promptsWithMultipleVersions}
                  valueStyle={{ color: '#0A84FF', fontSize: '24px' }}
                  suffix={
                    <span style={{ fontSize: '14px' }}>
                      {generalStats.promptCount > 0 
                        ? `(${Math.round((versionStats.promptsWithMultipleVersions / generalStats.promptCount) * 100)}%)`
                        : '(0%)'}
                    </span>
                  }
                />
              </Card>
            </Col>
            <Col span={24} style={{ marginTop: '8px' }}>
              <div style={{ padding: '8px 0' }}>
                <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  多版本提示词占比
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <Progress 
                      className="apple-progress"
                      percent={generalStats.promptCount > 0 
                        ? Math.round((versionStats.promptsWithMultipleVersions / generalStats.promptCount) * 100)
                        : 0
                      } 
                      strokeColor={{
                        '0%': '#0A84FF',
                        '100%': '#5AC8FA',
                      }}
                      showInfo={false}
                      strokeWidth={8}
                    />
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#0A84FF',
                    minWidth: '48px',
                    textAlign: 'right'
                  }}>
                    {generalStats.promptCount > 0 
                      ? Math.round((versionStats.promptsWithMultipleVersions / generalStats.promptCount) * 100)
                      : 0}%
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty 
            className="apple-empty"
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
            description="暂无版本数据"
          />
        )}
      </Card>
    </Spin>
  );

  // 渲染标签分析内容
  const renderTagAnalysis = () => (
    <Spin spinning={loading}>
      <Card className="apple-card fade-in delay-3" style={{ marginBottom: '24px' }}>
        <div className="title-container">
          <Text className="section-title title-purple">热门标签</Text>
          <Text className="subtitle">按使用频率</Text>
        </div>
        
        {tagStats.topTags.length > 0 ? (
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '10px',
            padding: '5px 0 15px'
          }}>
            {tagStats.topTags.map((tag, index) => (
              <Tooltip key={tag.id} title={`${tag.count} 个提示词使用了此标签`}>
                <Tag 
                  className="apple-tag"
                  color={tagColors[index % tagColors.length]} 
                >
                  <span style={{ marginRight: '6px', fontWeight: 500 }}>{tag.name}</span>
                  <Badge 
                    count={tag.count} 
                    style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                      color: tagColors[index % tagColors.length],
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      fontWeight: 'bold',
                      fontSize: '11px'
                    }} 
                  />
                </Tag>
              </Tooltip>
            ))}
          </div>
        ) : (
          <Empty 
            className="apple-empty"
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
            description="暂无标签数据"
          />
        )}
      </Card>
    </Spin>
  );

  // 渲染使用排行榜内容
  const renderUsageRanking = () => (
    <Spin spinning={loading}>
      <Card className="apple-card fade-in delay-4" style={{ marginBottom: '24px' }}>
        <div className="title-container">
          <Text className="section-title title-mixed">使用排行榜</Text>
          <Text className="subtitle">最常用的提示词</Text>
        </div>
        
        {mostUsedPrompts.length > 0 ? (
          <Table
            className="data-table"
            columns={columns}
            dataSource={mostUsedPrompts}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂无使用记录"
            className="apple-empty"
          />
        )}
      </Card>
    </Spin>
  );

  return (
    <div className="data-center-container">
      <div>
        {renderGeneralStats()}
        {renderVersionAnalysis()}
        {renderTagAnalysis()}
        {renderUsageRanking()}
      </div>
    </div>
  );
});

export default UsageStats;
